/**
 * "Matched from" — the customer's original mail, docked beside the triage table so a purchaser
 * checking a low-confidence match ("Engine oil filter FX-220", 44%) can see what the customer
 * actually sent WITHOUT losing their place in a 10-column table.
 *
 * ---- replaces SourceCard.tsx (2026-08-05) ----
 *
 * The old `SourceCard` read `GET /enquiries/:id/sources`, a mock-only `/api/v1` route with no
 * live equivalent — switched off via `lib/api/liveAvailability.ts`'s `enquirySources: false` and
 * rendering "Not available yet." on every real enquiry. Two live methods cover the same ground
 * with real data instead of a stub:
 *
 *   1. `enquiry.mail_id` — the `Communication` this enquiry was created from. Was never being
 *      SENT (see `lib/api/useEnquiryMutations.ts`'s `useCreateEnquiry` header for that half of the
 *      fix, "`mail_id` (2026-08-05)"), so every enquiry created before 2026-08-05 (and every one
 *      created manually, or via the "no mail" buttons on `/rfq`) genuinely has none — `null` here
 *      means "no source to show", not a fetch failure, and is rendered as a plain statement rather
 *      than an empty/hidden panel.
 *   2. `mail.get_email(mail_id)` — already live-verified (`useMail.ts`'s `useMailMessage`),
 *      returns `subject`, `sender`, `content` (the body) and `attachments[]`.
 *
 * ---- one panel, two ways to see it ----
 *
 * A modal was the obvious first idea and the wrong one — it would cover the very table the
 * purchaser is trying to verify a line against. Full side-by-side (splitting `RfqPage`'s main
 * column in two) was tried on paper and rejected too: the triage table already needs its own
 * horizontal scroll for ten columns (`RfqPage.tsx`'s `<table>`), and the xlsx preview
 * (`AttachmentPreview.tsx`'s `SheetGrid`) wants real width to read as a spreadsheet, not a
 * cropped strip — halving the page for both loses both.
 *
 * What's here instead: a DOCKED panel, `position: fixed` to the right edge of the viewport
 * (`.srcdock` in `global.css`), toggled open/closed rather than always present. It has no
 * backdrop — unlike `Modal`, the table underneath stays visible and interactive while the panel
 * is open, so a purchaser can scroll the lines with one hand and the source with the other. A
 * narrow viewport (`@media (max-width: 1100px)`) widens it to the full screen, the same
 * degradation `RfqPage`'s own bulk bar and header card already use at that breakpoint — side by
 * side stops being viable once there's no room for two things at once, so it stops pretending to
 * be side by side.
 *
 * Attachments reuse `AttachmentCard`/`AttachmentPreviewBody` from `mail/AttachmentPreview.tsx`
 * completely unmodified — the xlsx grid, image gallery and PDF viewer this screen would
 * otherwise have to rebuild. Clicking one shows it IN the dock (widened), with the line table
 * still beside it (Akhil, 2026-09-18) — it used to open Mail's centred modal over everything.
 */

import { useState } from 'react'
import { Badge } from '../../components/Badge'
import { Button, Card, Placeholder } from '../../components/ui'
import { AttachmentCard, AttachmentPreviewBody } from '../mail/AttachmentPreview'
import { htmlToText } from '../../lib/mail'
import { useMailMessage } from '../../lib/api/useMail'
import type { MailAccount, MailAttachment } from '../../lib/types'
import { FileLink } from '../../components/FileLink'
import type { EnquiryFile } from '../../lib/api/enquiryFiles'

/** `mail.get_email` hardcodes `account: 'customer'` on every response (defect B4 — see
 * `useMail.ts`'s `toMailDetail`), so this is never actually a guess; kept as a named constant
 * rather than a literal so the reason travels with the value. */
const MAIL_DETAIL_ACCOUNT: MailAccount = 'customer'

export function SourcePanel({
  mailId,
  uploads,
  onOpenMail,
}: {
  /** `enquiry.header.mail_id` — `null` when this enquiry has no known source mail. */
  mailId: string | null
  /**
   * Lists uploaded on the RFQ page and kept on the enquiry (`lib/api/enquiryFiles.ts`), newest
   * first. Shown above the mail, or on their own for an enquiry that never had one.
   */
  uploads: readonly EnquiryFile[]
  onOpenMail: (mailId: string, account: MailAccount) => void
}) {
  const [open, setOpen] = useState(false)
  // Disabled while closed — no point holding a poll running for a panel nobody is looking at
  // (`useMailMessage` polls, see `useMail.ts`'s `MAIL_POLL_MS`).
  const mailQuery = useMailMessage(open ? mailId : null)
  const [previewAttachment, setPreviewAttachment] = useState<MailAttachment | null>(null)
  const uploadCount = `${uploads.length} uploaded file${uploads.length === 1 ? '' : 's'}`

  if (!mailId && uploads.length === 0) {
    return (
      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <b>📎 Matched from</b>
          <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
            This enquiry wasn't created from a mail, so there's no source document to check these lines against.
          </span>
        </div>
      </Card>
    )
  }

  const mail = mailQuery.data ?? null

  return (
    <>
      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <b>📎 Matched from</b>
            <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
              {mailId ? 'check these lines against the mail they came from' : 'check these lines against the list they were uploaded from'}
              {mailId && uploads.length > 0 ? ` · ${uploadCount}` : ''}
            </span>
          </div>
          <Button size="sm" variant={open ? 'primary' : 'default'} onClick={() => setOpen((o) => !o)}>
            {open ? '✕ Hide source' : mailId ? '👁 View source mail' : `👁 View ${uploadCount}`}
          </Button>
        </div>
      </Card>

      {/*
        An attachment opens IN the dock (Akhil, 2026-09-18) — wider while one is showing, so a
        workbook or a PDF page is readable, and the line table stays beside it either way. It
        used to open the Mail screen's centred modal on top of everything.
      */}
      {open ? (
        <div className={`srcdock${previewAttachment ? ' srcdock-wide' : ''}`} role="complementary" aria-label={mailId ? 'Source mail' : 'Uploaded files'}>
          <div className="srcdock-head">
            {previewAttachment ? (
              <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <button type="button" className="linkish" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => setPreviewAttachment(null)}>
                  {mailId ? '← Mail' : '← Files'}
                </button>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📎 {previewAttachment.name}</div>
                  <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                    {previewAttachment.size}
                    {previewAttachment.preview_url ? (
                      <>
                        {' · '}
                        <FileLink href={previewAttachment.preview_url} name={previewAttachment.name}>
                          Download
                        </FileLink>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : !mailId ? (
              <b>{uploadCount}</b>
            ) : mailQuery.isPending ? (
              <b>Loading…</b>
            ) : mailQuery.isError ? (
              <b>Couldn't load this mail</b>
            ) : (
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {mail?.subject || '(no subject)'}
                </div>
                <div style={{ color: 'var(--muted)', fontSize: 12 }}>{mail?.from_name}</div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {mail && mailId ? (
                <Button size="sm" onClick={() => onOpenMail(mailId, MAIL_DETAIL_ACCOUNT)}>
                  Open in Mail ↗
                </Button>
              ) : null}
              <Button size="sm" onClick={() => setOpen(false)}>
                Close
              </Button>
            </div>
          </div>

          <div className="srcdock-body">
            {!previewAttachment && uploads.length > 0 ? (
              <div style={{ marginBottom: mailId ? 14 : 0 }}>
                <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700, marginBottom: 4 }}>
                  Uploaded on this enquiry
                </div>
                <div className="attgrid">
                  {uploads.map((file) => (
                    <AttachmentCard key={file.id} attachment={file} onPreview={() => setPreviewAttachment(file)} />
                  ))}
                </div>
              </div>
            ) : null}
            {previewAttachment ? (
              <AttachmentPreviewBody attachment={previewAttachment} />
            ) : !mailId ? null : mailQuery.isPending ? (
              <Placeholder>Loading the source mail…</Placeholder>
            ) : mailQuery.isError ? (
              <div style={{ color: 'var(--danger)', fontSize: 13 }}>
                {mailQuery.error instanceof Error ? mailQuery.error.message : 'Could not load this mail.'}
              </div>
            ) : mail ? (
              <>
                {mail.attachments.length > 0 ? (
                  <>
                    <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700, marginBottom: 4 }}>
                      {mail.attachments.length} attachment{mail.attachments.length === 1 ? '' : 's'}
                    </div>
                    <div className="attgrid">
                      {mail.attachments.map((attachment) => (
                        <AttachmentCard
                          key={attachment.id}
                          attachment={attachment}
                          onPreview={() => setPreviewAttachment(attachment)}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <Badge tone="muted">No attachments — items were read from the body below.</Badge>
                )}

                {/*
                  Rendered as TEXT, same reasoning `ReaderPane.tsx`'s `.rtext` uses: a mail body is
                  attacker-controlled, so `dangerouslySetInnerHTML` here would be a stored-XSS hole
                  in a screen whose whole job is opening untrusted mail. `white-space: pre-wrap`
                  (`.rtext` in global.css) keeps a plain-text body readable.
                */}
                <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700, margin: '12px 0 4px' }}>
                  Message body
                </div>
                <div className="rtext">{htmlToText(mail.body_html) || '(empty body)'}</div>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

    </>
  )
}
